import {
  BEvent,
  GeoJSONObject,
  Resource,
  TaskInfo,
  Location,
  ResourceLocation,
} from '@dispatch/shared/types';
import * as turf from '@turf/turf';
import { add, addDays, isAfter, isWithinInterval } from 'date-fns';

export const groupTaskReducer = (
  accumulator: Record<string, Partial<TaskInfo>[]>,
  currentValue: TaskInfo,
  _i: number,
  _a: TaskInfo[],
  key = `${currentValue?.lossLat}_${currentValue?.lossLong}`
) => {
  return (
    (accumulator[key] || (accumulator[key] = [])).push({
      ...currentValue,
    }),
    accumulator
  );
};

/**
 * itemToGeoJsonPoint takes a list of Task Summaries that have been grouped based on lat and lon and outputs
 * the data in a geoJson format.
 * @param item - array of task summary data. Can be 1 if no duplicates exists, and more if they do.
 * @returns geoJson
 */
export function itemToGeoJSONPoint([, item]: [
  string,
  (TaskInfo | BEvent)[]
]): GeoJSONObject {
  // we get first item in array just to associate the location with the group. All will have the same location, so its fine to use the first
  // if it isnt a group, and its just 1 item it will still be in an array, so the first item is the only in the array.
  const firstItem = item?.[0];
  const geometry: any = {
    type: 'Point',
    coordinates: [firstItem.lossLong || 0, firstItem.lossLat || 0],
  };
  const feature = turf.feature(geometry);

  feature.properties = { data: [...item], cluster: false };
  return feature;
}

const activeLocation = (location: Location[]) => {
  return location?.filter((location) => {
    const currentDate = new Date();

    if (!location.startDate) {
      return false;
    }

    // set location date to a year ahead of start date
    let locationEndDate = add(new Date(location.startDate), { years: 1 });

    // if there is a location end date then set it to location end date
    if (location.endDate) {
      locationEndDate = new Date(location.endDate);
    }

    // Check if startDate is after endDate
    if (isAfter(new Date(location.startDate), locationEndDate)) {
      // startDate is after endDate;
      return false;
    }

    // using sub and add days ensures that the interval is the correct interval for the start and end dates
    // we need it because isWithinInterval is not inclusive for the end date.
    let isActive = false;

    // if one of these dates is null this isWithinInterval will crash the system
    if (
      location.startDate &&
      location.endDate &&
      location.startDate !== location.endDate &&
      isAfter(new Date(currentDate), new Date(location.endDate))
    ) {
      isActive = isWithinInterval(currentDate, {
        start: new Date(location.startDate),
        end: addDays(locationEndDate, 1),
      });
    }

    return isActive;
  });
};

const getCircle = (location?: Location) => {
  // these are using any, because if not they make tests fail even when they make ts linter happy
  const center: any = [location?.long || 0, location?.lat || 0];
  const radius = location?.allowedTravelRadius || 0;
  const options: any = { units: 'miles' };
  const circle = turf.circle(center, radius, options);

  circle.properties = {
    center,
  };
  return circle;
};

export function ResourceLocationToGeoJSONPoint(resource: Resource): {
  activeWorkLocation: ResourceLocation[];
  activeStartingLocation: ResourceLocation;
} {
  const resourceLocations = resource.locations;

  const workingLocations = resourceLocations?.filter(
    (location) => !location.isStartingLocation
  );

  const startingLocation = resourceLocations?.filter(
    (location) => location.isStartingLocation
  );

  const activeWorking = activeLocation(workingLocations);

  const activeWorkLocation = activeWorking?.map((location: Location) => {
    return getCircle(location);
  });
  const activeStartingLocation = getCircle(
    activeLocation(startingLocation)?.[0]
  );

  return { activeWorkLocation, activeStartingLocation };
}
